import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface MarketPrice {
  lowest: number;
  average: number | null;
  currency: string;
  source: string;
  scrapedAt?: string;
  fromCache: boolean;
}

interface Args {
  brand?: string | null;
  model?: string | null;
  category?: string | null;
  enabled?: boolean;
}

const normalizeCategory = (c?: string | null): string => {
  if (!c) return "washing_machine";
  return c.toLowerCase().trim().replace(/[\s-]+/g, "_");
};

/**
 * Loads (and if missing, scrapes via tweakers.net) the market price for an
 * appliance model. Lookup is brand+model+category against new_appliance_prices.
 * The scrape edge function is only invoked when no price row exists.
 */
export function useApplianceMarketPrice({ brand, model, category, enabled = true }: Args) {
  const [price, setPrice] = useState<MarketPrice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !brand || !model) return;
    let cancelled = false;
    const cat = normalizeCategory(category);

    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // 1) Look up cached price first (no scrape if present)
        const { data: existing } = await supabase
          .from("new_appliance_models")
          .select("id, new_appliance_prices(lowest_price, average_price, currency, source, scraped_at)")
          .ilike("brand", brand)
          .ilike("model_name", model)
          .eq("category", cat)
          .order("scraped_at", { foreignTable: "new_appliance_prices", ascending: false })
          .limit(1)
          .maybeSingle();

        const priceRow = (existing as any)?.new_appliance_prices?.[0];
        if (priceRow && !cancelled) {
          setPrice({
            lowest: Number(priceRow.lowest_price),
            average: priceRow.average_price ? Number(priceRow.average_price) : null,
            currency: priceRow.currency || "EUR",
            source: priceRow.source || "tweakers.net",
            scrapedAt: priceRow.scraped_at,
            fromCache: true,
          });
          setIsLoading(false);
          return;
        }

        // 2) No cached price → invoke scraper
        const { data, error: fnErr } = await supabase.functions.invoke("scrape-tweakers-price", {
          body: { brand, model, category: cat },
        });
        if (cancelled) return;
        if (fnErr) {
          setError(fnErr.message);
          setIsLoading(false);
          return;
        }
        if (data?.error) {
          setError(String(data.error));
          setIsLoading(false);
          return;
        }
        if (typeof data?.lowest === "number") {
          setPrice({
            lowest: data.lowest,
            average: data.average ?? null,
            currency: data.currency || "EUR",
            source: data.source || "tweakers.net",
            scrapedAt: data.scrapedAt,
            fromCache: !!data.skipped,
          });
          if (!data.skipped) {
            toast.success(`Market price found: €${data.lowest.toFixed(2)}`, {
              description: `${data.offers ?? 0} offers on tweakers.net`,
            });
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [brand, model, category, enabled]);

  return { price, isLoading, error };
}
